import React from "react";
import { formatNumber } from "./passiveLoadHelpers";

const InfoIcon = ({ text }) => (
  <span className="metric-info-icon" title={text} aria-label={text}>
    <i className="bi bi-info-circle" />
  </span>
);

const HeaderWithInfo = ({ children, info, className = "" }) => (
  <th className={className}>
    <span className="header-with-info">
      <span>{children}</span>
      <InfoIcon text={info} />
    </span>
  </th>
);

const TitleWithInfo = ({ children, info }) => (
  <span className="title-with-info">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const LabelWithInfo = ({ children, info }) => (
  <span className="metric-label">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const getVariationLabel = (stdDev, mean) => {
  const std = Number(stdDev);
  const avg = Number(mean);

  if (!Number.isFinite(std) || !Number.isFinite(avg) || avg <= 0) {
    return "Sin variación relevante";
  }

  const ratio = std / avg;

  if (ratio < 0.25) return "Baja";
  if (ratio < 0.75) return "Media";
  return "Alta";
};

const PassiveStatisticsTable = ({ statistics, data }) => {
  const stats = statistics ?? data?.statistics ?? data?.stats ?? null;

  if (!stats || stats.count == null || stats.count < 2) return null;

  const { count, mean, median, stdDev, max, thresholdExcess } = stats;

  const variationLabel = getVariationLabel(stdDev, mean);

  const rows = [
    {
      label: "Períodos analizados",
      value: count,
      info: "Cantidad de períodos fuera de horario usados para calcular el comportamiento pasivo.",
    },
    {
      label: "Consumo promedio pasivo",
      value: `${formatNumber(mean)} kWh`,
      info: "Promedio del consumo fuera de horario analizado.",
    },
    {
      label: "Consumo habitual pasivo",
      value: `${formatNumber(median)} kWh`,
      info: "Valor típico del consumo fuera de horario. Evita que picos aislados distorsionen el análisis.",
    },
    {
      label: "Consumo máximo pasivo",
      value: `${formatNumber(max)} kWh`,
      info: "Mayor consumo detectado en un período fuera de horario.",
    },
    {
      label: "Variación del consumo pasivo",
      value: variationLabel,
      info: "Indica qué tan estable o variable fue el consumo fuera de horario.",
    },
    {
      label: "Límite para revisar",
      value: `${formatNumber(thresholdExcess)} kWh`,
      info: "Valor a partir del cual un período puede considerarse fuera de lo habitual y debe revisarse.",
    },
  ];

  return (
    <div className="passive-load-summary passive-statistics">
      <h3>
        <TitleWithInfo info="Indicadores usados para entender el consumo fuera del horario productivo.">
          Resumen del comportamiento pasivo fuera de horario
        </TitleWithInfo>
      </h3>

      <div className="passive-summary-table">
        <table>
          <thead>
            <tr>
              <HeaderWithInfo info="Indicador calculado para el análisis de consumo pasivo.">
                Indicador
              </HeaderWithInfo>

              <HeaderWithInfo
                className="text-right"
                info="Resultado calculado para el indicador."
              >
                Valor
              </HeaderWithInfo>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="concept-cell">
                  <LabelWithInfo info={row.info}>{row.label}</LabelWithInfo>
                </td>

                <td className="value-number">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PassiveStatisticsTable;